import React from 'react';
import { X } from 'lucide-react';
import { projectsAPI } from '../../services/api';
import { toast } from 'sonner';

const DetailPersonnelTab = ({
  projectId,
  selectedProjectLead, setSelectedProjectLead, projectLeads,
  selectedEstimator, setSelectedEstimator, estimators,
  estimatorAction, setEstimatorAction,
  selectedMarketingRep, setSelectedMarketingRep, marketingReps,
  selectedOfficeContact, setSelectedOfficeContact, officeContacts,
  officeAction, setOfficeAction,
  selectedProductionManager, setSelectedProductionManager, productionEmployees,
  pmCommissionPercent, setPmCommissionPercent,
  pmRows, setPmRows,
}) => {
  // Helpers to keep legacy single-PM fields in sync with the first row of pm_rows,
  // so existing reports (which read `production_manager` / `pm_commission_percent`)
  // continue to work.
  const updateRow = (idx, patch) => {
    const next = pmRows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    setPmRows(next);
    if (idx === 0) {
      if (patch.production_manager !== undefined) setSelectedProductionManager(patch.production_manager);
      if (patch.pm_commission_percent !== undefined) setPmCommissionPercent(patch.pm_commission_percent);
    }
  };
  const addRow = () => {
    setPmRows([...pmRows, { production_manager: '', pm_commission_percent: '20' }]);
  };
  const removeRow = (idx) => {
    const next = pmRows.filter((_, i) => i !== idx);
    const safe = next.length ? next : [{ production_manager: '', pm_commission_percent: '20' }];
    setPmRows(safe);
    // Re-sync legacy fields from new first row
    setSelectedProductionManager(safe[0].production_manager);
    setPmCommissionPercent(safe[0].pm_commission_percent);
  };

  const saveAllPMs = async () => {
    try {
      const payload = {
        production_manager: pmRows[0]?.production_manager || '',
        pm_commission_percent: pmRows[0]?.pm_commission_percent || '20',
        // Only save rows that actually have a PM employee selected; otherwise
        // they're placeholders and shouldn't show up in reports or contribute
        // to the project's commission cost.
        pm_rows: pmRows.filter(r => r.production_manager),
      };
      await projectsAPI.update(projectId, payload);
      toast.success('Production Managers saved');
    } catch {
      toast.error('Failed to save');
    }
  };

  return (
    <div className="bg-white rounded shadow p-6">
      <div className="grid grid-cols-2 gap-8">
        <div>
          <h2 className="text-xl text-gray-600 mb-6">Project Personnel</h2>
          <div className="space-y-6">
            <div>
              <label className="text-sm text-gray-600 mb-2 block">Project Lead:</label>
              <select value={selectedProjectLead} onChange={(e) => setSelectedProjectLead(e.target.value)} className="border border-gray-300 rounded px-3 py-2 w-full" data-testid="project-lead-select">
                <option value="">-- select --</option>
                {projectLeads.map((emp, i) => <option key={i} value={`${emp.last_name}, ${emp.first_name}`}>{emp.last_name}, {emp.first_name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-600 mb-2 block">Estimator Contact:</label>
              <div className="flex gap-2">
                <select value={selectedEstimator} onChange={(e) => setSelectedEstimator(e.target.value)} className="border border-gray-300 rounded px-3 py-2 flex-1" data-testid="estimator-select">
                  <option value="">-- select --</option>
                  {estimators.map((emp, i) => <option key={i} value={`${emp.last_name}, ${emp.first_name}`}>{emp.last_name}, {emp.first_name}</option>)}
                </select>
                <label className="flex items-center gap-2"><input type="checkbox" checked={estimatorAction} onChange={(e) => setEstimatorAction(e.target.checked)} className="w-4 h-4" /><span className="text-sm text-gray-600">Action</span></label>
              </div>
            </div>
            <div>
              <label className="text-sm text-gray-600 mb-2 block">Marketing Rep:</label>
              <select value={selectedMarketingRep} onChange={(e) => setSelectedMarketingRep(e.target.value)} className="border border-gray-300 rounded px-3 py-2 w-full" data-testid="marketing-rep-select">
                <option value="">-- select --</option>
                {marketingReps.map((emp, i) => <option key={i} value={`${emp.last_name}, ${emp.first_name}`}>{emp.last_name}, {emp.first_name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-600 mb-2 block">Office Contact:</label>
              <div className="flex gap-2">
                <select value={selectedOfficeContact} onChange={(e) => setSelectedOfficeContact(e.target.value)} className="border border-gray-300 rounded px-3 py-2 flex-1" data-testid="office-contact-select">
                  <option value="">-- select --</option>
                  {officeContacts.map((emp, i) => <option key={i} value={`${emp.last_name}, ${emp.first_name}`}>{emp.last_name}, {emp.first_name}</option>)}
                </select>
                <label className="flex items-center gap-2"><input type="checkbox" checked={officeAction} onChange={(e) => setOfficeAction(e.target.checked)} className="w-4 h-4" /><span className="text-sm text-gray-600">Action</span></label>
              </div>
            </div>
          </div>
        </div>
        <div>
          <h2 className="text-xl text-gray-600 mb-6">Production Managers</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2">
                  <th className="text-left p-2 text-gray-600 font-normal">Employee</th>
                  <th className="text-left p-2 text-gray-600 font-normal">% Commission</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {pmRows.map((row, idx) => (
                  <tr key={idx} className="border-b" data-testid={`pm-row-${idx}`}>
                    <td className="p-2">
                      <select
                        value={row.production_manager}
                        onChange={e => updateRow(idx, { production_manager: e.target.value })}
                        className="border border-gray-300 rounded px-3 py-2 w-full"
                        data-testid={`pm-employee-select-${idx}`}
                      >
                        <option value="">-- select --</option>
                        {productionEmployees.map((emp, i) => <option key={i} value={`${emp.last_name}, ${emp.first_name}`}>{emp.last_name}, {emp.first_name}</option>)}
                      </select>
                    </td>
                    <td className="p-2">
                      <input
                        type="text"
                        value={row.pm_commission_percent}
                        onChange={e => updateRow(idx, { pm_commission_percent: e.target.value })}
                        className="border border-gray-300 rounded px-3 py-2 w-20"
                        data-testid={`pm-commission-percent-${idx}`}
                      />
                    </td>
                    <td className="p-2">
                      <button
                        type="button"
                        onClick={() => removeRow(idx)}
                        className="bg-[#DC3545] hover:bg-[#C82333] text-white rounded p-1.5"
                        data-testid={`remove-pm-btn-${idx}`}
                        title="Remove this PM row"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-4 flex items-center gap-3">
              <button
                type="button"
                onClick={addRow}
                className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-4 py-2 rounded transition-colors"
                data-testid="add-pm-btn"
              >
                Add PM
              </button>
              <button
                type="button"
                onClick={saveAllPMs}
                className="bg-gray-700 hover:bg-gray-800 text-white px-4 py-2 rounded transition-colors"
                data-testid="save-pms-btn"
              >
                Save PMs
              </button>
              {pmRows.length > 1 && (
                <span className="text-xs text-gray-500 italic">
                  {pmRows.length} PMs · shares should sum to 100%
                  {Math.abs(pmRows.reduce((s, r) => s + (parseFloat(r.pm_commission_percent) || 0), 0) - 100) > 0.01 && (
                    <span className="text-amber-600"> (currently {pmRows.reduce((s, r) => s + (parseFloat(r.pm_commission_percent) || 0), 0)}%)</span>
                  )}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailPersonnelTab;
